/**
 * Comprehensive FAQ Page
 * Categories: General, Consultation, Surgery, Recovery, Insurance
 */

import React, { useState } from 'react';
import { useSite } from '../contexts/SiteContext';
import { CTASection } from '../components/CTASection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  HelpCircle, 
  Stethoscope, 
  Calendar, 
  Syringe, 
  HeartPulse, 
  CreditCard,
  Search,
  ChevronRight
} from 'lucide-react';

export const FAQPage = () => {
  const { theme, doctor, bodyPart } = useSite();
  const [activeCategory, setActiveCategory] = useState('general');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    { id: 'general', label: 'General Questions', icon: HelpCircle },
    { id: 'consultation', label: 'Consultation & Diagnosis', icon: Stethoscope },
    { id: 'treatment', label: 'Treatment Options', icon: Syringe },
    { id: 'surgery', label: 'Surgery & Procedures', icon: Calendar },
    { id: 'recovery', label: 'Recovery & Rehabilitation', icon: HeartPulse },
    { id: 'practical', label: 'Practical & Insurance', icon: CreditCard },
  ];

  const faqData = {
    general: [
      {
        question: 'Who is Dr. Deepthi Nandan Reddy?',
        answer: 'Dr. Deepthi Nandan Reddy is a senior consultant orthopaedic and upper limb surgeon with over 30 years of experience. He completed his advanced fellowships in Shoulder, Elbow & Hand surgery in the UK (Leicester), Mayo Clinic, and Florida Orthopaedic Institute (USA). He holds FRCS (Ortho) from the UK and has treated over 15,000 patients and performed 5,000+ surgeries.'
      },
      {
        question: 'What conditions does Dr. Reddy treat?',
        answer: 'Dr. Reddy specializes in all upper limb conditions including: Shoulder problems (rotator cuff tears, frozen shoulder, dislocations, arthritis, fractures), Elbow conditions (tennis elbow, golfer\'s elbow, stiffness, fractures), and Hand & Wrist issues (carpal tunnel, trigger finger, fractures, nerve injuries). He is one of few surgeons in India with comprehensive upper limb training.'
      },
      {
        question: 'Where does Dr. Reddy practice?',
        answer: 'Dr. Reddy practices at Yashoda Hospitals, HITEC City, Hyderabad. The hospital is equipped with state-of-the-art facilities including advanced imaging, modern operation theatres, and comprehensive rehabilitation services.'
      },
      {
        question: 'What makes Dr. Reddy\'s approach different?',
        answer: 'Dr. Reddy combines international fellowship training with evidence-based protocols. He emphasizes patient education, clear communication, and shared decision-making. Every treatment plan is personalized, and he ensures patients understand all options before proceeding with any intervention.'
      },
      {
        question: 'Does Dr. Reddy provide second opinions?',
        answer: 'Yes, Dr. Reddy welcomes patients seeking second opinions. Many patients come to him after consulting elsewhere to get another perspective on their condition and treatment options. He provides honest, evidence-based opinions without any obligation to proceed with treatment.'
      }
    ],
    consultation: [
      {
        question: 'How do I book an appointment?',
        answer: 'You can book an appointment through: 1) Online booking at Yashoda Hospitals website, 2) Calling +91 99599 64567, 3) WhatsApp to the same number, or 4) Through the hospital\'s appointment desk. We typically accommodate appointments within 1-3 days.'
      },
      {
        question: 'What should I bring to my first consultation?',
        answer: 'Please bring: 1) Any previous medical records related to your condition, 2) X-rays, MRI, or other imaging reports (both physical films/CDs and reports), 3) List of current medications, 4) Insurance details if applicable, 5) A list of questions you want to ask.'
      },
      {
        question: 'How long is a typical consultation?',
        answer: 'First consultations typically take 30-45 minutes to allow thorough examination and discussion. Follow-up appointments are usually 15-20 minutes. We believe in giving each patient adequate time to address all concerns.'
      },
      {
        question: 'Will I need any tests after the consultation?',
        answer: 'Depending on your condition, you may need X-rays, MRI, ultrasound, or blood tests. Many basic investigations can be done at the same hospital visit. Dr. Reddy will explain exactly what tests are needed and why.'
      },
      {
        question: 'Can I have a video consultation?',
        answer: 'Video consultations are available for initial assessments, follow-ups, and second opinions. However, some conditions require in-person examination for accurate diagnosis. The team will advise if a physical visit is necessary.'
      },
      {
        question: 'How quickly can I get my test results?',
        answer: 'X-ray results are typically available immediately. MRI results take 24-48 hours. Blood tests are usually ready within 24 hours. Results are explained to you in person or via a follow-up call.'
      }
    ],
    treatment: [
      {
        question: 'Does every condition require surgery?',
        answer: 'No, many orthopaedic conditions can be effectively managed with non-surgical treatments including physiotherapy, medications, lifestyle modifications, and injections. Surgery is recommended only when conservative treatment fails or for specific conditions where it offers clear benefits.'
      },
      {
        question: 'What non-surgical treatments are available?',
        answer: 'Non-surgical options include: Physiotherapy and exercises, Pain medications (NSAIDs), Corticosteroid injections, Hyaluronic acid injections, Platelet-rich plasma (PRP) therapy, Activity modification and ergonomic advice, Splints or braces, and Ultrasound-guided interventions.'
      },
      {
        question: 'What is PRP therapy and is it effective?',
        answer: 'PRP (Platelet-Rich Plasma) therapy uses your own blood platelets to promote healing. It shows promising results for tendon problems like tennis elbow and rotator cuff tendinitis. However, evidence varies by condition. Dr. Reddy will advise if PRP is appropriate for your specific situation.'
      },
      {
        question: 'How do I know if I need surgery?',
        answer: 'Surgery is typically considered when: 1) Conservative treatment has failed after adequate trial (usually 3-6 months), 2) The condition is worsening, 3) There\'s significant structural damage (like complete tendon tears), 4) Quality of life is severely affected, or 5) Delay might compromise results.'
      },
      {
        question: 'What are the latest treatment techniques available?',
        answer: 'Dr. Reddy uses cutting-edge techniques including: Arthroscopic (keyhole) surgery, Ultrasound-guided injections, Latest implant designs for joint replacement, Minimally invasive fracture fixation, Nerve release techniques, and Regenerative medicine approaches where evidence supports them.'
      }
    ],
    surgery: [
      {
        question: 'What is arthroscopic (keyhole) surgery?',
        answer: 'Arthroscopic surgery uses small cameras and instruments through tiny incisions (5-10mm) instead of large open cuts. Benefits include less pain, faster recovery, lower infection risk, and better cosmetic results. Most shoulder, elbow, and wrist surgeries can be performed arthroscopically.'
      },
      {
        question: 'How long does surgery typically take?',
        answer: 'Surgery duration varies by procedure: Simple arthroscopic procedures (30-60 minutes), Rotator cuff repair (1-2 hours), Shoulder replacement (2-3 hours), Fracture fixation (1-2 hours). You\'ll receive specific information about your procedure during pre-operative counseling.'
      },
      {
        question: 'What type of anesthesia is used?',
        answer: 'Most upper limb surgeries use general anesthesia combined with a nerve block (regional anesthesia) for post-operative pain control. This combination provides excellent pain relief for 12-24 hours after surgery. The anesthesiologist will discuss options with you.'
      },
      {
        question: 'How long will I stay in the hospital?',
        answer: 'Many procedures are done as day surgery (discharge same day). Rotator cuff repairs typically require 1 night stay. Joint replacements require 2-3 days. Complex procedures may require longer stays. Dr. Reddy prefers early mobilization when safe.'
      },
      {
        question: 'What are the risks of surgery?',
        answer: 'All surgeries carry some risk including infection (1-2%), bleeding, nerve injury, stiffness, and anesthesia risks. Specific risks depend on the procedure. Dr. Reddy discusses all risks thoroughly during consent, and his complication rates are well below published averages.'
      },
      {
        question: 'When can I go back to work after surgery?',
        answer: 'This depends on your job and the surgery performed. Desk work: 1-2 weeks. Light manual work: 6-8 weeks. Heavy manual work: 3-6 months. We provide specific guidance and fitness-for-work certificates when needed.'
      }
    ],
    recovery: [
      {
        question: 'What is the typical recovery timeline?',
        answer: 'Recovery varies by procedure: Minor arthroscopic procedures (4-6 weeks), Rotator cuff repair (4-6 months), Shoulder replacement (6-12 months for full recovery), Fractures (6-12 weeks for bone healing). These are general timelines—individual recovery depends on many factors.'
      },
      {
        question: 'Will I need physiotherapy?',
        answer: 'Yes, physiotherapy is crucial for most orthopaedic conditions, both surgical and non-surgical. We work with experienced physiotherapists who understand upper limb rehabilitation. A structured rehabilitation program significantly improves outcomes.'
      },
      {
        question: 'How often will I need follow-up appointments?',
        answer: 'Typical follow-up schedule: 2 weeks (wound check), 6 weeks (X-ray review), 3 months (progress assessment), 6 months, and 1 year. Additional visits are scheduled as needed. We remain available via WhatsApp for questions between visits.'
      },
      {
        question: 'What exercises should I do after surgery?',
        answer: 'Exercises are prescribed specifically for your surgery and recovery stage. Never start exercises without guidance. Your physiotherapist will teach you the correct techniques, and we provide written/video instructions for home exercises.'
      },
      {
        question: 'When can I drive after surgery?',
        answer: 'You cannot drive while wearing a sling or if you cannot control the vehicle safely. Typically: After minor procedures (2-3 weeks), After major shoulder surgery (6-8 weeks), After hand/wrist surgery (when grip strength returns). Always check with your insurance.'
      },
      {
        question: 'When can I return to sports?',
        answer: 'Return to sports depends on the procedure and sport: Non-contact sports (3-4 months after most surgeries), Contact sports (6-9 months after shoulder stabilization), Golf/tennis (4-6 months after rotator cuff repair). Premature return risks re-injury.'
      }
    ],
    practical: [
      {
        question: 'What is the consultation fee?',
        answer: 'Please contact Yashoda Hospitals directly for current consultation fees. The hospital has transparent pricing, and fees vary based on the complexity of consultation. Initial consultations may cost more than follow-ups.'
      },
      {
        question: 'Do you accept health insurance?',
        answer: 'Yes, Dr. Reddy practices at Yashoda Hospitals which is empaneled with most major insurance companies including HDFC Ergo, ICICI Lombard, Star Health, and others. Contact the hospital\'s insurance desk for specific coverage details.'
      },
      {
        question: 'How much does surgery cost?',
        answer: 'Surgery costs depend on the procedure, implants used (if any), hospital stay duration, and insurance coverage. Yashoda Hospitals provides detailed cost estimates before surgery. For insured patients, the TPA desk handles approvals and paperwork.'
      },
      {
        question: 'Is there parking available?',
        answer: 'Yes, Yashoda Hospitals HITEC City has ample parking facilities for both two-wheelers and four-wheelers. Valet parking is also available. The hospital is well-connected by public transport and rideshare services.'
      },
      {
        question: 'What are the hospital timings?',
        answer: 'Dr. Reddy\'s OPD timings are Monday to Saturday, 9 AM to 5 PM at Yashoda Hospitals HITEC City. Emergency services are available 24/7. It\'s best to book an appointment to avoid waiting.'
      },
      {
        question: 'Can I get medical records or reports sent to me?',
        answer: 'Yes, medical records can be provided upon request. Digital copies of X-rays, MRI, and reports can be shared via email or the hospital\'s patient portal. Physical copies are available from the medical records department.'
      }
    ]
  };

  // Filter FAQs based on search query
  const filteredFAQs = searchQuery.trim() 
    ? Object.values(faqData).flat().filter(
        faq => 
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqData[activeCategory];

  return (
    <div className="min-h-screen" data-testid="faq-page">
      {/* Hero Section */}
      <section 
        className="py-16 md:py-20"
        style={{ 
          background: `linear-gradient(135deg, ${theme?.primaryHex || '#3b82f6'} 0%, ${theme?.primaryHex || '#3b82f6'}dd 100%)`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <HelpCircle className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Find answers to common questions about consultations, treatments, surgery, and recovery
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-white/50"
              data-testid="faq-search"
            />
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Category Sidebar */}
            {!searchQuery && (
              <div className="lg:w-1/4">
                <div className="sticky top-24">
                  <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
                  <div className="space-y-2">
                    {categories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <button
                          key={category.id}
                          onClick={() => setActiveCategory(category.id)}
                          className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${
                            activeCategory === category.id 
                              ? 'text-white shadow-md' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          style={activeCategory === category.id ? { backgroundColor: theme?.primaryHex || '#3b82f6' } : {}}
                          data-testid={`faq-category-${category.id}`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-sm font-medium">{category.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Quick Contact Card */}
                  <Card className="mt-8 border-0 shadow-md">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Still have questions?</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">
                        Can&apos;t find the answer you&apos;re looking for? Contact us directly.
                      </p>
                      <a 
                        href={doctor?.whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 font-medium text-sm hover:underline"
                        style={{ color: theme?.primaryHex || '#3b82f6' }}
                      >
                        💬 WhatsApp Us
                        <ChevronRight className="w-4 h-4" />
                      </a>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* FAQ Content */}
            <div className={searchQuery ? 'w-full' : 'lg:w-3/4'}>
              {searchQuery && (
                <div className="mb-6">
                  <p className="text-gray-600">
                    {filteredFAQs.length} results for &ldquo;<span className="font-medium">{searchQuery}</span>&rdquo;
                  </p>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="text-sm hover:underline mt-2"
                    style={{ color: theme?.primaryHex || '#3b82f6' }}
                  >
                    Clear search
                  </button>
                </div>
              )}

              {!searchQuery && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {categories.find(c => c.id === activeCategory)?.label}
                  </h2>
                  <p className="text-gray-600">
                    {faqData[activeCategory]?.length} questions in this category
                  </p>
                </div>
              )}

              <Accordion type="single" collapsible className="space-y-4">
                {filteredFAQs?.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="border border-gray-200 rounded-xl px-6 bg-white hover:border-gray-300 transition-colors shadow-sm"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {filteredFAQs?.length === 0 && (
                <div className="text-center py-12">
                  <HelpCircle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No matching questions found.</p>
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="mt-4 hover:underline"
                    style={{ color: theme?.primaryHex || '#3b82f6' }}
                  >
                    View all FAQs
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default FAQPage;
